import {Text} from 'src/design/atoms/Text';
import type {MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {InspectionScheduleResponse} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {ActiveInactivePills} from 'src/design/atoms/pillPresets';
import {serverComputedColumn} from 'src/design/organisms/serverComputedColumn';
import {useGetClientSelectOptionsQuery} from 'src/redux/queries/client';
import {useGetInspectionTypeSelectOptionsQuery} from 'src/redux/queries/inspectionType';
import {useGetSiteSelectOptionsQuery} from 'src/redux/queries/site';
import {ActiveStateDataOptions} from 'src/shared/constants/columnFilterData';
import {InspectionStateEnumPill} from 'src/shared/constants/PillConstants';
import {
  cadenceLabel,
  formatSiteLocalDate,
  isScheduleOverdue,
  selectOptionsToData,
} from './inspectionScheduleHelpers';
import {ScheduleHealthPills} from './ScheduleHealthPills';

const EMPTY_SELECT_QUERY = {
  start: 0,
  length: 10000,
  filters: [],
  filterFns: [],
  sorting: [],
};

export interface UseInspectionScheduleColumnsOptions {
  /** Adds the Site and Client columns and loads their server-filter options. */
  includeSite?: boolean;
}

/**
 * Column set shared by the Inspections Schedules tab and the Site page card.
 * @registry MRT columns for inspection recurrence schedules (cycle status, cadence, issues).
 */
export const useInspectionScheduleColumns = ({
  includeSite = true,
}: UseInspectionScheduleColumnsOptions = {}): MRT_ColumnDef<InspectionScheduleResponse>[] => {
  const {data: sites} = useGetSiteSelectOptionsQuery(EMPTY_SELECT_QUERY, {
    skip: !includeSite,
  });
  const {data: clients} = useGetClientSelectOptionsQuery(EMPTY_SELECT_QUERY, {
    skip: !includeSite,
  });
  // Both tables expose the type filter, so these options load either way.
  const {data: inspectionTypes} =
    useGetInspectionTypeSelectOptionsQuery(EMPTY_SELECT_QUERY);

  const siteFilterData = useMemo(() => selectOptionsToData(sites?.data), [sites]);
  const clientFilterData = useMemo(() => selectOptionsToData(clients?.data), [clients]);
  const typeFilterData = useMemo(
    () => selectOptionsToData(inspectionTypes?.data),
    [inspectionTypes]
  );

  return useMemo<MRT_ColumnDef<InspectionScheduleResponse>[]>(() => {
    const siteColumns: MRT_ColumnDef<InspectionScheduleResponse>[] = includeSite
      ? [
          {
            id: 'siteId',
            accessorFn: row => String(row.siteId),
            header: 'Site',
            enableColumnFilterModes: false,
            filterVariant: 'multi-select',
            mantineFilterSelectProps: {data: siteFilterData},
            Cell: ({row}) => <Text size="sm">{row.original.siteName}</Text>,
          },
          serverComputedColumn<InspectionScheduleResponse>({
            id: 'site.clientId',
            accessorFn: row => (row.clientId ? String(row.clientId) : ''),
            header: 'Client',
            filterable: true,
            enableColumnFilterModes: false,
            filterVariant: 'multi-select',
            mantineFilterSelectProps: {data: clientFilterData},
            Cell: ({row}) => row.original.clientName ?? '—',
          }),
        ]
      : [];

    return [
      serverComputedColumn<InspectionScheduleResponse>({
        id: 'currentCycle',
        header: 'Cycle Status',
        accessorFn: row => row.latestInspectionState ?? '',
        Cell: ({row}) =>
          row.original.latestInspectionState ? (
            <Pill
              {...InspectionStateEnumPill(row.original.latestInspectionState)}
            />
          ) : (
            <Text size="sm" c="dimmed">
              None yet
            </Text>
          ),
      }),
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {data: ActiveStateDataOptions},
        Cell: ({row}) => <Pill {...ActiveInactivePills(row.original.active)} />,
      },
      serverComputedColumn<InspectionScheduleResponse>({
        id: 'health',
        header: 'Issues',
        accessorFn: row => row.healthIssues.length,
        Cell: ({row}) => (
          <ScheduleHealthPills schedule={row.original} emptyText="—" />
        ),
      }),
      ...siteColumns,
      {
        id: 'inspectionTypeId',
        accessorFn: row => String(row.inspectionTypeId),
        header: 'Inspection Type',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {data: typeFilterData},
        Cell: ({row}) => row.original.inspectionTypeName,
      },
      {
        id: 'cadence',
        accessorFn: cadenceLabel,
        header: 'Cadence',
        enableColumnFilter: false,
      },
      {
        id: 'leadTimeDays',
        accessorFn: row => `${row.leadTimeDays} days`,
        header: 'Lead Time',
        enableColumnFilter: false,
      },
      serverComputedColumn<InspectionScheduleResponse>({
        id: 'latestInspectionDueAt',
        header: 'Cycle Due (site local)',
        accessorFn: row => row.latestInspectionDueAt ?? '',
        Cell: ({row}) => {
          const overdue = isScheduleOverdue(row.original);
          return (
            <Text
              size="sm"
              c={overdue ? 'red' : undefined}
              fw={overdue ? 500 : undefined}>
              {formatSiteLocalDate(
                row.original.latestInspectionDueAt,
                row.original.siteTimeZone
              )}
            </Text>
          );
        },
      }),
      {
        id: 'nextDueAt',
        accessorFn: row =>
          formatSiteLocalDate(row.nextDueAt, row.siteTimeZone),
        header: 'Next Due (site local)',
        enableColumnFilter: false,
      },
      {
        id: 'nextGenerationAt',
        accessorFn: row =>
          formatSiteLocalDate(row.nextGenerationAt, row.siteTimeZone),
        header: 'Generates On (site local)',
        enableColumnFilter: false,
      },
      serverComputedColumn<InspectionScheduleResponse>({
        id: 'generatedCount',
        header: 'Generated',
        accessorFn: row => row.generatedCount,
      }),
    ];
  }, [includeSite, siteFilterData, clientFilterData, typeFilterData]);
};
